import { createSignal } from "solid-js";

/**
 * Shared "select images to save" mode. Tapping the header button enters
 * selection mode; tapping chat images then toggles them; a Save button
 * hands the chosen batch to the native share sheet (iOS "Save N Images" ->
 * Photos) or downloads them.
 */
const [selectionMode, setSelectionMode] = createSignal(false);
const [selected, setSelected] = createSignal<string[]>([]);

export { selectionMode };

export function isSelected(url?: string) {
  return !!url && selected().includes(url);
}

export function toggleSelection(url?: string) {
  if (!url) return;
  setSelected((cur) =>
    cur.includes(url) ? cur.filter((u) => u !== url) : [...cur, url],
  );
}

export function selectionCount() {
  return selected().length;
}

export function enterSelectionMode() {
  setSelected([]);
  setSelectionMode(true);
}

export function exitSelectionMode() {
  setSelectionMode(false);
  setSelected([]);
}

/**
 * Save every selected image at once, then leave selection mode.
 */
export async function saveSelectedImages() {
  const urls = selected();
  if (urls.length === 0) {
    exitSelectionMode();
    return;
  }

  const isTouch = matchMedia("(pointer: coarse)").matches;
  try {
    const files = await Promise.all(
      urls.map(async (url, i) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
        return new File([blob], `image-${i + 1}.${ext}`, { type: blob.type });
      }),
    );

    if (isTouch && navigator.canShare && navigator.canShare({ files })) {
      await navigator.share({ files });
    } else {
      for (const file of files) {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(file);
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        await new Promise((r) => setTimeout(r, 150));
      }
    }
  } catch {
    /* user cancelled the share sheet, or a fetch failed */
  }
  exitSelectionMode();
}
